<template>
  <UserLayout>
    <section class="">
      <h3 class="font-semibold text-3xl leading-10 tracking-tight mb-8 mt-2">Edit Profile</h3>
      <div class="space-y-4 text-gray-700">
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div class="w-full px-2 md:w-1/3">
            <label class="block mb-1 required text-gray-smoke font-medium text-sm md:text-lg mb-2">First Name</label>
            <input
              class="focus:ring-0 focus:outline-offset-0 w-full text-gray-slate pl-3 pr-3 h-10 md:h-12 border-1 border-borderColor focus:outline-none rounded-md"
              type="text"
              v-model="$v.f_name.$model"
              :classStr="{ 'ring-red-700': $v.f_name.$error }"
            />
            <div v-if="$v.f_name.$error">
              <span class="text-xs text-red-700" v-if="!$v.f_name.required">
                First Name is required
              </span>
            </div>
          </div>
          <div class="w-full px-2 md:w-1/3">
            <label class="block mb-1 required text-gray-smoke font-medium text-sm md:text-lg mb-2">Last Name</label>
            <input
              class="w-full text-gray-slate pl-3 pr-3 h-10 md:h-12 border-1 border-borderColor focus:outline-none rounded-md"
              type="text"
              v-model="$v.l_name.$model"
              :classStr="{ 'ring-red-700': $v.l_name.$error }"
            />
            <div v-if="$v.l_name.$error">
              <span class="text-xs text-red-700" v-if="!$v.l_name.required">
                Last Name is required
              </span>
            </div>
          </div>
          <div class="w-full px-2 md:w-1/3">
            <label class="block mb-1 text-gray-smoke font-medium text-sm md:text-lg mb-2">Date of Birth</label>
            <DropdownDatepicker
              :dropdownClass="'text-gray-slate w-full pl-3 pr-3 h-10 md:h-12 border-1 border-borderColor focus:outline-none rounded-md'"
              display-format="mdy"
              v-model="$v.selectedDate.$model"
              v-bind:required="true"
              v-bind:min-age="13"
              :default-date="selectedDate"
              submit-format="dd-mm-yyyy"
            ></DropdownDatepicker>
            <div v-if="$v.selectedDate.$error">
              <span
                class="text-xs text-red-700"
                v-if="!$v.selectedDate.required"
              >
                Date of Birth is required
              </span>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full user-contact">
            <label class="block mb-1 text-gray-smoke font-medium text-sm md:text-lg mb-2">Mobile Number</label>
            <VuePhoneNumberInput
              v-model="$v.mobile.$model"
              color="#e2e8f0"
              :error="$v.nationalMobile.$error"
              :no-example="true"
              @update="
                (nationalMobile = $event.nationalNumber),
                  (isd_code = $event.countryCode)
              "
              :default-country-code="isd_code"
            />
            <div v-if="$v.nationalMobile.$error">
              <span
                class="text-xs text-red-700"
                v-if="!$v.nationalMobile.required"
              >
                Mobile number is required
              </span>
              <span
                class="text-xs text-red-700"
                v-if="!$v.nationalMobile.minLength"
              >
                Mobile must have at least 10 digits.
              </span>
              <span
                class="text-xs text-red-700"
                v-if="!$v.nationalMobile.maxLength"
              >
                Mobile must have at Most 10 digits.
              </span>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div class="w-full px-2 md:w-1/3">
            <LanguageInput
              :languagesList="languagesList"
              :languagesData="languagesData"
              :languages="languages"
              @LanguagesChange="handleLanguagesChange"
              @LanguagesRemove="handleLanguagesRemove"
            />
          </div>
          <div class="w-full px-2 md:w-1/3">
            <CategoryInput
              :categoriesList="categoriesList"
              :categoriesData="categoriesData"
              :category_id="category_id"
              @CategoriesChange="handleCategoryChange"
              @CategoriesRemove="handleCategoryRemove"
            />
          </div>
          <div class="w-full px-2 md:w-1/3">
            <label class="block mb-1 required text-gray-smoke font-medium text-sm md:text-lg mb-2">Update your Timezone</label>
            <model-list-select
              :list="getTimezoneDetails"
              v-model="selectedTimezone"
              option-value="id"
              option-text="display_name"
              placeholder="Select item"
              class="text-gray-slate w-full pl-3 pr-3 h-10 md:h-12 border-1 border-borderColor focus:outline-none rounded-md"
            >
            </model-list-select>
          </div>
        </div>
        
        <div
          class="flex space-x-6 justify-end"
          role="group"
          aria-label="Button group"
        >
          <button
            type="button"
            @click="userProfileRedirect"
            class="h-10 px-5 text-green-light transition-colors duration-150 bg-white rounded-lg focus:shadow-outline hover:bg-green-light hover:text-white ring-1 ring-green-light"
          >
            Back
          </button>
          <button
            type="submit"
            class="h-10 px-5 text-indigo-100 transition-colors duration-150 bg-green-light rounded-lg focus:shadow-outline hover:bg-green-light"
            @click="onclickProfileUpdate"
          >
            Save
          </button>
        </div>
      </div>
    </section>
  </UserLayout>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ModelListSelect } from "vue-search-select";
import "vue-search-select/dist/VueSearchSelect.css";
import UserLayout from "@/components/layouts/UserLayout";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import LanguageInput from "@/components/t-components/LanguageInput";
import CategoryInput from "@/components/t-components/CategoryInput";
import { maxLength, minLength, required } from "vuelidate/lib/validators";
import DropdownDatepicker from "@/components/vue-dropdown-datepicker/dropdown-datepicker";

export default {
  name: "UserProfileEdit",
  components: {
    UserLayout,
    LanguageInput,
    CategoryInput,
    ModelListSelect,
    VuePhoneNumberInput,
    DropdownDatepicker,
  },
  data() {
    return {
      f_name: null,
      l_name: null,
      selectedDate: null,
      mobile: null,
      nationalMobile: null,
      isd_code: null,
      languages: null,
      languagesData: [],
      category_id: null,
      categoriesData: [],
      selectedTimezone: null,
      currentPage: 1,
      personal_details: [],
    };
  },
  validations: {
    f_name: {
      required,
    },
    l_name: {
      required,
    },
    selectedDate: {
      // required,
    },
    languages: {
      required,
    },
    languagesData: {
      required,
    },
    category_id: {
      required,
    },
    categoriesData: {
      required,
      maxLength: maxLength(4),
    },
    mobile: {
      // required,
      minLength: minLength(10),
    },
    nationalMobile: {
      // required,
      maxLength: maxLength(10),
      minLength: minLength(10),
    },
  },
  computed: {
    ...mapGetters([
      "languagesList",
      "categoriesList",
      "getTimezoneDetails",
      "userProfile",
    ]),
  },
  methods: {
    ...mapActions([
      "getLanguagesList",
      "getCategoriesList",
      "timezone",
      "userProfileUpdate",
      "getUserProfile",
    ]),

    handleLanguagesChange(event) {
      const data = {
        id: event.target.value,
        text: event.target.options[event.target.options.selectedIndex].text,
      };
      if (data.id == "") {
        return;
      }
      const index = this.languagesData.findIndex((e) => e.id === data.id);
      if (index === -1) {
        this.languagesData.push(data);
        this.languages = data.id;
      }
    },

    handleCategoryChange(event) {
      const data = {
        id: event.target.value,
        text: event.target.options[event.target.options.selectedIndex].text,
      };
      if (data.id == "") {
        return;
      }
      const index = this.categoriesData.findIndex((e) => e.id === data.id);
      if (index === -1) {
        this.categoriesData.push(data);
        this.category_id = data.id;
      }
    },

    handleLanguagesRemove(id) {
      const index = this.languagesData.findIndex((e) => e.id === id);
      if (index !== -1) {
        this.languagesData.splice(index, 1);
        this.languages = this.languagesData[0].id;
      }
    },

    handleCategoryRemove(id) {
      const index = this.categoriesData.findIndex((e) => e.id === id);
      if (index !== -1) {
        this.categoriesData.splice(index, 1);
        this.category_id = this.categoriesData[0].id;
      }
    },

    async onclickProfileUpdate() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        console.log("invalid");
        this.$store.commit('setError', {title: "Alert", text: "Please fill all valid details."})
      } else {
        const data = new FormData();

        data.append("f_name", this.f_name);
        data.append("l_name", this.l_name);
        data.append("dob", this.selectedDate);
        if (this.mobile) {
          data.append("isd_code", this.isd_code);
          data.append("mobile", this.nationalMobile);
        }
        for (var i = 0; i < this.languagesData.length; i++) {
          data.append("language_id[" + i + "]", this.languagesData[i].id);
        }
        for (var j = 0; j < this.categoriesData.length; j++) {
          data.append("category_id[" + j + "]", this.categoriesData[j].id);
        }
        data.append("timezone", this.selectedTimezone);
        await this.userProfileUpdate(data).then(() => {
          this.userProfileRedirect()
        })
      }
    },
    userProfileRedirect () {
      this.$router.push({ name: "UserProfile" });
    },
    getLanguagesData(ids, names) {
      const idsArray = ids.split(",");
      const namesArray = names.split(",");
      idsArray.forEach((e, i) => {
        this.languagesData.push({ id: e, text: namesArray[i] });
      });
    },

    getCategoriesData(ids, names) {
      const idsArray = ids && ids.split(",");
      const namesArray = names && names.split(",");
      idsArray &&
        idsArray.forEach((e, i) => {
          this.categoriesData.push({ id: e, text: namesArray[i] });
        });
    },
  },
  async created() {
    await this.getLanguagesList();
    await this.getCategoriesList();
    await this.timezone();
  },
  async mounted() {
    let query = {
      page: this.currentPage,
    };
    await this.getUserProfile({ query });
    this.personal_details =
      this.userProfile && this.userProfile.personal_details;
    this.f_name = this.personal_details && this.personal_details.f_name;
    this.l_name = this.personal_details && this.personal_details.l_name;
    this.nationalMobile = this.personal_details && this.personal_details.mobile;
    this.mobile = this.personal_details && this.personal_details.mobile;
    this.isd_code = this.personal_details && this.personal_details.isd_code || 'US';
    this.selectedDate = this.personal_details && this.personal_details.dob;
    this.languages =
      this.personal_details &&
      this.personal_details.language_ids &&
      this.personal_details.language_ids.split(",") &&
      this.personal_details.language_ids.split(",")[0];
    this.personal_details &&
      this.personal_details.language_ids &&
      this.getLanguagesData(
        this.personal_details.language_ids,
        this.personal_details.language_names
      );
    this.category_id =
      this.personal_details &&
      this.personal_details.category_ids &&
      this.personal_details.category_ids.split(",") &&
      this.personal_details.category_ids.split(",")[0];
    this.personal_details &&
      this.personal_details.category_ids &&
      this.getCategoriesData(
        this.personal_details.category_ids,
        this.personal_details.category_name
      );
    this.selectedTimezone =
      this.personal_details && this.personal_details.timezone;
  },
};
</script>

<style>
  .ui.selection.dropdown>.delete.icon, 
  .ui.selection.dropdown>.dropdown.icon, 
  .ui.selection.dropdown>.search.icon{
    top: 14px;
  }
  .ui.fluid.dropdown{
    padding-top: 17px;
  }
  .user-contact .input-tel__input {
    font-size: 1rem !important;
    line-height: 1.5rem !important;
    cursor: text !important;
    box-shadow: none !important;
  }
  .user-contact .input-tel__input::placeholder  {
    color: #adadad;
  }
</style>